import React, { useCallback } from 'react';
import { Waypoint } from 'react-waypoint';
import FolderCard from '../FolderCard';
import FoldersHeader from '../FoldersHeader';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import FolderContent from '../FolderContent';
import { MeasuredCookieType } from '@wix/da-shared-react/pkg/Measured';
import Grid, {
  GridElement,
  GridBreakpoint,
} from '@wix/da-shared-react/pkg/Grid';
import StickyFolderBar from '../FolderBar/StickyFolderBar';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import { useNonEmptyGallectionFolders } from '../hooks/useGallectionFolders';

const BREAKPOINTS: GridBreakpoint[] = [
  {
    maxWidth: 480,
    elementWidth: 280,
    elementHeight: 250,
  },
  {
    maxWidth: 1024,
    elementWidth: 304,
    elementHeight: 250,
  },
  {
    maxWidth: 1280,
    elementWidth: 389,
    elementHeight: 298,
  },
  {
    maxWidth: 1366,
    elementWidth: 404,
    elementHeight: 306,
  },
  {
    maxWidth: 1920,
    elementWidth: 438,
    elementHeight: 325,
  },
];

export interface Props {}

const GallectionsGridView: React.FC<Props> = () => {
  const {
    folders,
    hasMore,
    hasLess,
    isFetching,
    fetchNext,
    fetchPrevious,
    selectedFolderId,
  } = useNonEmptyGallectionFolders();

  const renderElement = useCallback(
    ({ index, style, width }: GridElement) => {
      const folder = folders[index];
      return (
        <div key={folder.folderId} style={{ ...style }}>
          <FolderCard folder={folder} indexInsideParent={index} width={width} />
        </div>
      );
    },
    [folders]
  );

  const getItemId = useCallback(
    (index: number) => {
      const folder = folders[index];
      return `${index}-${folder.folderId}`;
    },
    [folders]
  );

  if (selectedFolderId) {
    return (
      <div>
        <ErrorBoundary>
          <StickyFolderBar withBackButton={true} />
          <FolderContent
            scrollIntoViewWhenLoaded
            folderId={selectedFolderId!}
          />
        </ErrorBoundary>
      </div>
    );
  }

  if (!folders.length && !hasMore && !hasLess) {
    return null;
  }

  return (
    <div>
      <ErrorBoundary>
        <FoldersHeader />
        {hasLess && (
          <Waypoint onEnter={fetchPrevious} horizontal topOffset="-20%" />
        )}
        <Grid
          elementCount={folders.length}
          cookieType={MeasuredCookieType.USER_PROFILE_GALLECTION_CONTENT}
          renderElement={renderElement}
          getElementId={getItemId}
          breakpoints={BREAKPOINTS}
          enableScrollOptim={false}
        />
        {isFetching && <LoadingIndicator />}
        {hasMore && (
          <Waypoint onEnter={fetchNext} horizontal bottomOffset="-20%" />
        )}
      </ErrorBoundary>
    </div>
  );
};
GallectionsGridView.displayName = 'GallectionsGridView';

export default GallectionsGridView;
